import React, { ReactNode, useContext, useMemo } from 'react';
import EDMenuButton from '../../../../../../BaseComponents/Buttons/MenuButton/EDMenuButton';
import MenuButtonItem from '../../../../../../BaseComponents/Buttons/MenuButton/EDMenuButtonItem';
import {
  TEDEKBokforderTYPEnum,
  TEDEKBokforderVariantEnum,
} from '../../../../../../globalQueryTypes';
import { ICustomAddButtonProps } from '../../../../_Common/Const/Delsystem';
import { DefaultBfoVariantEnum } from '../../../ObjektVyer/Bokforder/Components/BokforderContext/BokforderContextManager';
import { EK_BOKFORDER_UTANORDNING_ACTIVE } from '../Const/BokforderSysoptionConsts';
import { BokforderWrapperContext } from '../Contexts/BokforderWrapperContext';
import { CreateDialogOpenEnum } from './BokforderProviderWrapper';

interface IProps extends ICustomAddButtonProps {
  children?: ReactNode;
}

const bokforderMenuButtonCategory = 'Skapa ny bokföringsorder';
const utanordningMenuButtonCategory = 'Skapa ny utanordning';

export const CustomBfoAddButton: React.FC<React.PropsWithChildren<unknown>> = (
  props: IProps
) => {
  const bokforderWrapperContext = useContext(BokforderWrapperContext);

  const isUtanordningActive = useMemo(() => {
    return (
      bokforderWrapperContext.sysOptions?.find(sysOption => {
        return sysOption.OptionKey === EK_BOKFORDER_UTANORDNING_ACTIVE;
      })?.OptionData === 'Y'
    );
  }, [bokforderWrapperContext.sysOptions]);

  const canCreateBokforder = useMemo(
    () =>
      bokforderWrapperContext.currentUser &&
      (bokforderWrapperContext.currentUser?.BESTALLARE_BOKFORDER ||
        bokforderWrapperContext.isEconomyPersonnel),
    [
      bokforderWrapperContext.currentUser,
      bokforderWrapperContext.isEconomyPersonnel,
    ]
  );

  const canCreateUtanordning = useMemo(
    () =>
      bokforderWrapperContext.currentUser &&
      bokforderWrapperContext.currentUser?.BESTALLARE_UTANORDNING,
    [bokforderWrapperContext.currentUser]
  );

  const isDisabled = useMemo(() => {
    return (
      bokforderWrapperContext.currentUserDataLoading ||
      (!canCreateBokforder && !canCreateUtanordning)
    );
  }, [
    bokforderWrapperContext.currentUserDataLoading,
    canCreateBokforder,
    canCreateUtanordning,
  ]);

  const isUtanordningBestallare = useMemo(() => {
    return (
      !bokforderWrapperContext.currentUserDataLoading &&
      bokforderWrapperContext.currentUser &&
      bokforderWrapperContext.currentUser.BESTALLARE_UTANORDNING
    );
  }, [
    bokforderWrapperContext.currentUser,
    bokforderWrapperContext.currentUserDataLoading,
  ]);

  const onMenuButtonClick = (
    type: TEDEKBokforderTYPEnum,
    variant: TEDEKBokforderVariantEnum | DefaultBfoVariantEnum
  ) => {
    if (props.setNewObject) {
      props.setNewObject(undefined, { type, variant });
    }

    bokforderWrapperContext.setCreateOptionDialogOpen(
      CreateDialogOpenEnum.OPENFROMLIST
    );
  };

  return (
    <EDMenuButton
      icon="add"
      iconSize="default"
      tooltip="Skapa ny"
      buttonLabel={props.smallMenu ? 'Skapa ny' : undefined}
      captionLabel={props.smallMenu}
      color="inherit"
      padding="normal"
      margin="none"
      disabled={isDisabled}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 22,
        horizontal: 5,
      }}
      testId={props.testId}
      renderButton={
        props.renderButton
          ? ({ onClick, disabled, icon, label }) =>
              props.renderButton!({
                onClick,
                disabled,
                icon,
                label,
              }) as React.ReactElement
          : undefined
      }
    >
      <MenuButtonItem
        category={bokforderMenuButtonCategory}
        disabled={!canCreateBokforder}
        onClick={() =>
          onMenuButtonClick(
            TEDEKBokforderTYPEnum.BOKFORINGSORDER,
            TEDEKBokforderVariantEnum.TOM
          )
        }
      >
        Tom
      </MenuButtonItem>
      <MenuButtonItem
        category={bokforderMenuButtonCategory}
        disabled={!canCreateBokforder}
        onClick={() =>
          onMenuButtonClick(
            TEDEKBokforderTYPEnum.BOKFORINGSORDER,
            TEDEKBokforderVariantEnum.MALL
          )
        }
      >
        Använd mall
      </MenuButtonItem>
      <MenuButtonItem
        category={bokforderMenuButtonCategory}
        disabled={!canCreateBokforder}
        onClick={() =>
          onMenuButtonClick(
            TEDEKBokforderTYPEnum.BOKFORINGSORDER,
            DefaultBfoVariantEnum.VERIFIKATION
          )
        }
      >
        Från verifikation
      </MenuButtonItem>
      {isUtanordningActive && isUtanordningBestallare && (
        <MenuButtonItem
          category={utanordningMenuButtonCategory}
          onClick={() =>
            onMenuButtonClick(
              TEDEKBokforderTYPEnum.UTANORDNING,
              TEDEKBokforderVariantEnum.UTANORDINGBANKONTO
            )
          }
        >
          Bankkonton
        </MenuButtonItem>
      )}

      {isUtanordningActive && isUtanordningBestallare && (
        <MenuButtonItem
          category={utanordningMenuButtonCategory}
          onClick={() =>
            onMenuButtonClick(
              TEDEKBokforderTYPEnum.UTANORDNING,
              TEDEKBokforderVariantEnum.UTANORDINGPGBG
            )
          }
        >
          PG/BG
        </MenuButtonItem>
      )}

      {isUtanordningActive && isUtanordningBestallare && (
        <MenuButtonItem
          category={utanordningMenuButtonCategory}
          onClick={() =>
            onMenuButtonClick(
              TEDEKBokforderTYPEnum.UTANORDNING,
              TEDEKBokforderVariantEnum.UTANORDNINGTILLGODO
            )
          }
        >
          Tillgodohavande
        </MenuButtonItem>
      )}
    </EDMenuButton>
  );
};

export default CustomBfoAddButton;
